import { createAppError } from '../../api/errors';
import type { MutationResult, ReasonInput, StatusReasonInput } from '../../api/contract';
import { partnerCredentialFixtures, partnerFixtures, partnerIpAllowlistFixtures } from './fixtures';
import type { PartnerRepository } from './repository';
import type {
  ChangePartnerStatusInput,
  CreatePartnerInput,
  CredentialExpiryInput,
  IpAllowlistEntry,
  IpAllowlistInput,
  IssueCredentialInput,
  OneTimeSecretResult,
  PartnerCredential,
  PartnerDetail,
  PartnerListQuery,
  UpdatePartnerInput,
} from './types';

export interface MockPartnerRepositoryOptions {
  readonly mode?: 'data' | 'empty' | 'error';
}

// Stable, human-readable route identifiers (used by demo links and route-level tests) resolve to
// the first fixture without duplicating fixture rows or altering list/pagination totals.
const partnerDetailAliases: Readonly<Record<string, string>> = {
  'partner-a': partnerFixtures[0]!.id,
};

export class MockPartnerRepository implements PartnerRepository {
  readonly #mode: 'data' | 'empty' | 'error';
  #partners: PartnerDetail[];
  #credentials: PartnerCredential[];
  #allowlist: IpAllowlistEntry[];
  #credentialSequence = 1;
  #allowlistSequence = 1;

  constructor(options: MockPartnerRepositoryOptions = {}) {
    this.#mode = options.mode ?? 'data';
    this.#partners = this.#mode === 'empty' ? [] : partnerFixtures.map((value) => ({ ...value }));
    this.#credentials =
      this.#mode === 'empty' ? [] : partnerCredentialFixtures.map((value) => ({ ...value }));
    this.#allowlist =
      this.#mode === 'empty' ? [] : partnerIpAllowlistFixtures.map((value) => ({ ...value }));
  }

  async list(query: PartnerListQuery, signal: AbortSignal) {
    this.#assertAvailable(signal);
    const filtered = this.#partners
      .filter((partner) => matchesPartner(partner, query))
      .sort((left, right) => comparePartners(left, right, query));
    const start = Math.max(0, query.page - 1) * query.pageSize;
    return {
      items: filtered.slice(start, start + query.pageSize).map((value) => ({ ...value })),
      total: filtered.length,
      requestId: 'mock-partners-list',
    };
  }

  async get(partnerId: string, signal: AbortSignal): Promise<PartnerDetail> {
    this.#assertAvailable(signal);
    return { ...this.#findPartner(partnerId) };
  }

  create(input: CreatePartnerInput): Promise<MutationResult<PartnerDetail>> {
    this.#assertAvailable();
    if (this.#partners.some((partner) => partner.code === input.code.toUpperCase())) {
      return Promise.reject(conflict('Partner code already exists', 'mock-partner-conflict'));
    }
    const now = '2026-07-23T12:00:00+07:00';
    const partner: PartnerDetail = {
      id: mockUuid(400 + this.#partners.length),
      code: input.code.toUpperCase(),
      name: input.name,
      centerAgentId: null,
      affiliateCommissionExpiryDays:
        input.affiliateCommissionExpiryDays === undefined
          ? 30
          : input.affiliateCommissionExpiryDays,
      defaultCurrencyCode: (input.defaultCurrencyCode ?? 'THB').toUpperCase(),
      timezone: input.timezone ?? 'Asia/Bangkok',
      usernameNormalizationMode: input.usernameNormalizationMode ?? 'LOWERCASE_NFKC',
      status: 'SUSPENDED',
      requestRateLimitPerMinute: input.requestRateLimitPerMinute ?? 100,
      withdrawalRateLimitPerMinute: input.withdrawalRateLimitPerMinute ?? 10,
      timestampToleranceSeconds: input.timestampToleranceSeconds ?? 300,
      createdAt: now,
      createdBy: mockActor,
      updatedAt: now,
      updatedBy: mockActor,
    };
    this.#partners = [partner, ...this.#partners];
    return Promise.resolve(result(partner, 'mock-partner-create'));
  }

  update(partnerId: string, input: UpdatePartnerInput): Promise<MutationResult<PartnerDetail>> {
    this.#assertAvailable();
    const current = this.#findPartner(partnerId);
    const updated: PartnerDetail = {
      ...current,
      ...(input.name === undefined ? {} : { name: input.name }),
      ...(input.affiliateCommissionExpiryDays === undefined
        ? {}
        : { affiliateCommissionExpiryDays: input.affiliateCommissionExpiryDays }),
      ...(input.defaultCurrencyCode === undefined
        ? {}
        : { defaultCurrencyCode: input.defaultCurrencyCode.toUpperCase() }),
      ...(input.timezone === undefined ? {} : { timezone: input.timezone }),
      ...(input.usernameNormalizationMode === undefined
        ? {}
        : { usernameNormalizationMode: input.usernameNormalizationMode }),
      ...(input.requestRateLimitPerMinute === undefined
        ? {}
        : { requestRateLimitPerMinute: input.requestRateLimitPerMinute }),
      ...(input.withdrawalRateLimitPerMinute === undefined
        ? {}
        : { withdrawalRateLimitPerMinute: input.withdrawalRateLimitPerMinute }),
      ...(input.timestampToleranceSeconds === undefined
        ? {}
        : { timestampToleranceSeconds: input.timestampToleranceSeconds }),
      updatedAt: '2026-07-23T12:10:00+07:00',
      updatedBy: mockActor,
    };
    this.#replacePartner(updated);
    return Promise.resolve(result(updated, 'mock-partner-update'));
  }

  changeStatus(
    partnerId: string,
    input: ChangePartnerStatusInput,
  ): Promise<MutationResult<PartnerDetail>> {
    this.#assertAvailable();
    const current = this.#findPartner(partnerId);
    if (current.status === 'CLOSED' && input.status !== 'CLOSED') {
      return Promise.reject(conflict('Closed Partners cannot be reopened', 'mock-partner-status'));
    }
    const updated = {
      ...current,
      status: input.status,
      updatedAt: '2026-07-23T12:20:00+07:00',
      updatedBy: mockActor,
    };
    this.#replacePartner(updated);
    return Promise.resolve(result(updated, 'mock-partner-status'));
  }

  async listCredentials(
    partnerId: string,
    signal: AbortSignal,
  ): Promise<readonly PartnerCredential[]> {
    this.#assertAvailable(signal);
    this.#findPartner(partnerId);
    return this.#credentials
      .filter((credential) => credential.partnerId === partnerId)
      .map((credential) => ({ ...credential }));
  }

  issueCredential(partnerId: string, input: IssueCredentialInput): Promise<OneTimeSecretResult> {
    this.#assertAvailable();
    this.#findPartner(partnerId);
    return Promise.resolve(this.#createCredential(partnerId, input.expiresAt ?? null, 'I'));
  }

  rotateCredential(
    partnerId: string,
    credentialId: string,
    _input: ReasonInput,
  ): Promise<OneTimeSecretResult> {
    this.#assertAvailable();
    const current = this.#findCredential(partnerId, credentialId);
    return Promise.resolve(this.#createCredential(partnerId, current.expiresAt, 'R'));
  }

  changeCredentialExpiry(
    partnerId: string,
    credentialId: string,
    input: CredentialExpiryInput,
  ): Promise<MutationResult<PartnerCredential>> {
    this.#assertAvailable();
    const current = this.#findCredential(partnerId, credentialId);
    const updated = { ...current, expiresAt: input.expiresAt };
    this.#replaceCredential(updated);
    return Promise.resolve(result(updated, 'mock-credential-expiry'));
  }

  revokeCredential(
    partnerId: string,
    credentialId: string,
    input: ReasonInput,
  ): Promise<MutationResult<PartnerCredential>> {
    this.#assertAvailable();
    const current = this.#findCredential(partnerId, credentialId);
    const updated = {
      ...current,
      status: 'REVOKED' as const,
      revokedAt: '2026-07-23T12:30:00+07:00',
      revokedBy: mockActor,
      revocationReason: input.reason,
    };
    this.#replaceCredential(updated);
    return Promise.resolve(result(updated, 'mock-credential-revoke'));
  }

  async listIpAllowlist(
    partnerId: string,
    signal: AbortSignal,
  ): Promise<readonly IpAllowlistEntry[]> {
    this.#assertAvailable(signal);
    this.#findPartner(partnerId);
    return this.#allowlist
      .filter((entry) => entry.partnerId === partnerId)
      .map((entry) => ({ ...entry }));
  }

  createIpAllowlistEntry(
    partnerId: string,
    input: IpAllowlistInput,
  ): Promise<MutationResult<IpAllowlistEntry>> {
    this.#assertAvailable();
    this.#findPartner(partnerId);
    if (
      this.#allowlist.some(
        (entry) => entry.partnerId === partnerId && entry.network === input.network,
      )
    ) {
      return Promise.reject(conflict('Network already exists', 'mock-ip-conflict'));
    }
    const timestamp = '2026-07-23T12:40:00+07:00';
    const entry: IpAllowlistEntry = {
      id: mockUuid(500 + this.#allowlistSequence++),
      partnerId,
      network: requiredNetwork(input.network),
      status: 'ACTIVE',
      description: input.description,
      createdAt: timestamp,
      createdBy: mockActor,
      updatedAt: timestamp,
      updatedBy: mockActor,
    };
    this.#allowlist = [...this.#allowlist, entry];
    return Promise.resolve(result(entry, 'mock-ip-create'));
  }

  updateIpAllowlistEntry(
    partnerId: string,
    entryId: string,
    input: IpAllowlistInput,
  ): Promise<MutationResult<IpAllowlistEntry>> {
    this.#assertAvailable();
    const current = this.#findAllowlist(partnerId, entryId);
    const updated = {
      ...current,
      description: input.description,
      updatedAt: '2026-07-23T12:50:00+07:00',
      updatedBy: mockActor,
    };
    this.#replaceAllowlist(updated);
    return Promise.resolve(result(updated, 'mock-ip-update'));
  }

  changeIpAllowlistStatus(
    partnerId: string,
    entryId: string,
    input: StatusReasonInput,
  ): Promise<MutationResult<IpAllowlistEntry>> {
    this.#assertAvailable();
    const current = this.#findAllowlist(partnerId, entryId);
    const status = partnerStatus(input.status);
    const updated = {
      ...current,
      status,
      updatedAt: '2026-07-23T13:00:00+07:00',
      updatedBy: mockActor,
    };
    this.#replaceAllowlist(updated);
    return Promise.resolve(result(updated, 'mock-ip-status'));
  }

  #createCredential(
    partnerId: string,
    expiresAt: string | null,
    secretMarker: string,
  ): OneTimeSecretResult {
    const sequence = this.#credentialSequence++;
    const credential: PartnerCredential = {
      id: mockUuid(600 + sequence),
      partnerId,
      keyId: `pk_${String(sequence).padStart(32, 'A')}`,
      status: 'ACTIVE',
      validFrom: '2026-07-23T13:10:00+07:00',
      expiresAt,
      revokedAt: null,
      revokedBy: null,
      revocationReason: null,
      lastUsedAt: null,
      createdAt: '2026-07-23T13:10:00+07:00',
      createdBy: mockActor,
    };
    this.#credentials = [...this.#credentials, credential];
    const secret = `ps_${secretMarker.repeat(43)}`;
    return { credential: { ...credential }, secret, requestId: `mock-credential-${sequence}` };
  }

  #assertAvailable(signal?: AbortSignal): void {
    if (signal?.aborted === true) throw new DOMException('Partner request aborted', 'AbortError');
    if (this.#mode === 'error') {
      throw createAppError({
        status: 503,
        code: 'MOCK_PARTNERS_UNAVAILABLE',
        message: 'Partner fixture is unavailable',
        requestId: 'mock-partners-error',
        fieldErrors: {},
        retryAfterSeconds: null,
      });
    }
  }

  #findPartner(partnerId: string): PartnerDetail {
    const resolvedId = partnerDetailAliases[partnerId] ?? partnerId;
    const partner = this.#partners.find((candidate) => candidate.id === resolvedId);
    if (partner === undefined) throw notFound('Partner not found');
    return partner;
  }

  #findCredential(partnerId: string, credentialId: string): PartnerCredential {
    const credential = this.#credentials.find(
      (candidate) => candidate.id === credentialId && candidate.partnerId === partnerId,
    );
    if (credential === undefined) throw notFound('Credential not found');
    return credential;
  }

  #findAllowlist(partnerId: string, entryId: string): IpAllowlistEntry {
    const entry = this.#allowlist.find(
      (candidate) => candidate.id === entryId && candidate.partnerId === partnerId,
    );
    if (entry === undefined) throw notFound('IP allowlist entry not found');
    return entry;
  }

  #replacePartner(partner: PartnerDetail): void {
    this.#partners = this.#partners.map((candidate) =>
      candidate.id === partner.id ? partner : candidate,
    );
  }

  #replaceCredential(credential: PartnerCredential): void {
    this.#credentials = this.#credentials.map((candidate) =>
      candidate.id === credential.id ? credential : candidate,
    );
  }

  #replaceAllowlist(entry: IpAllowlistEntry): void {
    this.#allowlist = this.#allowlist.map((candidate) =>
      candidate.id === entry.id ? entry : candidate,
    );
  }
}

function matchesPartner(partner: PartnerDetail, query: PartnerListQuery): boolean {
  const filters = query.filters;
  return (
    includes(partner.code, filters.code) &&
    includes(partner.name, filters.name) &&
    (filters.status.length === 0 || partner.status === filters.status) &&
    (filters.currency.length === 0 ||
      partner.defaultCurrencyCode === filters.currency.toUpperCase())
  );
}

function comparePartners(
  left: PartnerDetail,
  right: PartnerDetail,
  query: PartnerListQuery,
): number {
  const comparison = String(left[query.sort]).localeCompare(String(right[query.sort]));
  return query.order === 'asc' ? comparison : -comparison;
}

function includes(value: string, filter: string): boolean {
  return filter.length === 0 || value.toLocaleLowerCase().includes(filter.toLocaleLowerCase());
}

function result<T>(data: T, requestId: string): MutationResult<T> {
  return { data: { ...data }, requestId };
}

function conflict(message: string, requestId: string) {
  return createAppError({
    status: 409,
    code: 'MOCK_CONFLICT',
    message,
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

function notFound(message: string) {
  return createAppError({
    status: 404,
    code: 'MOCK_NOT_FOUND',
    message,
    requestId: 'mock-partner-not-found',
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

function partnerStatus(value: string): 'ACTIVE' | 'SUSPENDED' | 'CLOSED' {
  if (value === 'ACTIVE' || value === 'SUSPENDED' || value === 'CLOSED') return value;
  throw new RangeError(`Unsupported Partner status: ${value}`);
}

function mockUuid(sequence: number): string {
  return `00000000-0000-4000-8000-${String(sequence).padStart(12, '0')}`;
}

function requiredNetwork(network: string | undefined): string {
  if (network === undefined || network.trim().length === 0) {
    throw new RangeError('IP allowlist network is required');
  }
  return network;
}

const mockActor = '00000000-0000-4000-8000-000000000001';
